"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, MessageSquare, ShieldCheck, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const reassurancePoints = [{
  title: "No-obligation site visits",
  description: "Clear recommendations tailored to your home layout.",
  icon: CalendarCheckIcon
}, {
  title: "NASC & CHAS",
  description: "Verified safety compliance and full insurance coverage.",
  icon: ShieldCheck
}, {
  title: "Respectful domestic crews",
  description: "Careful setup around gardens, driveways, and pathways.",
  icon: CheckCircle2
}];
function CalendarCheckIcon(props: React.ComponentProps<typeof Clock>) {
  return <Clock {...props} />;
}
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="request-a-quote" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-28 border-t border-border">
      {/* Structural Accent Line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-1 bg-primary rounded-full" />

      {/* Decorative Grid Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(28,34,44,0.7),transparent)] pointer-events-none" />

      <div className="container mx-auto px-5 md:px-8 relative z-10 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col items-center text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-4">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-secondary text-secondary-foreground border border-border/80">
              <span className="size-1.5 rounded-full bg-primary" />
              Residential Scaffolding Services
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground max-w-3xl">
            Get a clear, fixed quote for your home project
          </motion.h2>

          {/* Supporting Copy */}
          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl font-sans leading-relaxed">
            Tell us what you are building or repairing. We visit your property, provide practical advice, and give you a straightforward price with no hidden costs.
          </motion.p>

          {/* Action Trio Buttons */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center justify-center gap-3.5 w-full sm:w-auto">
            {/* Primary Action */}
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 py-3 h-12 shadow-sm rounded-md transition-colors">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                Request a quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            {/* Direct Call Button */}
            <Button asChild variant="secondary" size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border font-medium px-6 py-3 h-12 rounded-md transition-colors">
              <a href="tel:01344929900" className="inline-flex items-center gap-2 text-secondary-foreground">
                <Phone className="size-4 text-primary shrink-0" />
                <span className="text-secondary-foreground font-medium">Call now</span>
                <span className="text-xs text-muted-foreground ml-1">{"(Mon–Fri 8am–6pm)"}</span>
              </a>
            </Button>

            {/* Project Discussion Button */}
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground text-foreground font-medium px-6 py-3 h-12 rounded-md transition-colors">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <MessageSquare className="size-4 text-muted-foreground" />
                Discuss a project
              </Link>
            </Button>
          </motion.div>

          {/* Feature and reassurance grid */}
          <motion.div variants={itemVariants} className="mt-14 w-full grid grid-cols-1 sm:grid-cols-3 gap-4 pt-10 border-t border-border/80 text-left">
            {reassurancePoints.map((point, index) => {
            const IconComp = point.icon;
            return <div key={point.title} data-index={index} className="p-5 rounded-lg bg-card/60 border border-border/60 backdrop-blur-xs flex flex-col gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-md bg-secondary text-primary">
                      <IconComp className="size-4" />
                    </div>
                    <h3 className="font-semibold text-sm sm:text-base text-card-foreground">
                      {point.title}
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-normal pl-9">
                    {point.description}
                  </p>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}